import { ArrowSquareOutIcon, XIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { Button } from '@/components/ui/Button';
import { KeyHint } from '@/components';
import {
    usePipPlayerStore,
    selectCanGoNext,
    selectCanGoPrev,
} from '@/stores/pip-player.store';
import { I18nNamespace } from '@/lib/i18n-namespaces';

const topBarButtonClassName =
    'h-8 px-2 text-white-100 hover:bg-white/10 hover:text-white-100 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed';
const topBarIconButtonClassName =
    'h-8 w-8 px-0 text-white-100 hover:bg-white/10 hover:text-white-100 cursor-pointer';
interface PipPlayerTopBarProps {
    sourceName: string;
}

export function PipPlayerTopBar({ sourceName }: PipPlayerTopBarProps) {
    const { t } = useTranslation(I18nNamespace.COMMON);
    const close = usePipPlayerStore((s) => s.close);
    const goNext = usePipPlayerStore((s) => s.goNext);
    const goPrev = usePipPlayerStore((s) => s.goPrev);
    const canGoNext = usePipPlayerStore(selectCanGoNext);
    const canGoPrev = usePipPlayerStore(selectCanGoPrev);
    const hasNavigation = usePipPlayerStore(
        (s) => s.navigationContext !== null,
    );
    const mode = usePipPlayerStore((s) => s.mode);
    const eventId = usePipPlayerStore((s) => s.eventId);
    const sourceId = usePipPlayerStore((s) => s.sourceId);

    const detailsPath =
        mode === 'source' && sourceId
            ? `/sources/${sourceId}`
            : mode === 'report-event' && eventId
              ? `/reports/events/${eventId}`
              : mode === 'event' && eventId
                ? `/events/${eventId}`
                : null;

    const openDetails = () => {
        if (detailsPath) {
            window.open(detailsPath, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <div
            data-pip-drag-handle
            className="absolute inset-x-0 top-0 z-[2] cursor-grab opacity-0 transition-opacity duration-200 active:cursor-grabbing group-hover/pip:opacity-100"
            onClick={(e) => e.stopPropagation()}
        >
            <div className="absolute inset-x-0 top-0 h-12 pointer-events-none bg-[linear-gradient(179.82deg,#111214_0.15%,rgba(17,18,20,0)_83.43%)]" />

            <div className="relative flex h-12 items-center justify-between px-3 py-2">
                <div className="flex h-8 items-center gap-2">
                    {hasNavigation && (
                        <>
                            <Button
                                type="button"
                                variant="ghost"
                                size="medium"
                                shape="square"
                                onClick={goPrev}
                                disabled={!canGoPrev}
                                className={topBarButtonClassName}
                            >
                                {t('button.previous')}
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                size="medium"
                                shape="square"
                                onClick={goNext}
                                disabled={!canGoNext}
                                className={topBarButtonClassName}
                            >
                                {t('button.next')}
                            </Button>
                        </>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                type="button"
                                variant="ghost"
                                size="medium"
                                shape="square"
                                onClick={openDetails}
                                disabled={!detailsPath}
                                className={topBarIconButtonClassName}
                                aria-label={
                                    mode === 'source'
                                        ? t('button.openSourceDetails', {
                                              name: sourceName,
                                          })
                                        : t('button.openEventDetails', {
                                              name: sourceName,
                                          })
                                }
                            >
                                <ArrowSquareOutIcon
                                    className="size-4"
                                    weight="regular"
                                />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top" sideOffset={8}>
                            {t('button.openDetails')}
                        </TooltipContent>
                    </Tooltip>

                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                type="button"
                                variant="ghost"
                                size="medium"
                                shape="square"
                                onClick={close}
                                className={topBarIconButtonClassName}
                                aria-label={t('button.close')}
                            >
                                <XIcon className="size-4" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top" sideOffset={8}>
                            {t('button.close')}
                            <KeyHint theme="light" size="esc">
                                Esc
                            </KeyHint>
                        </TooltipContent>
                    </Tooltip>
                </div>
            </div>
        </div>
    );
}
